import { AdjustmentsHorizontalIcon, ArrowUpIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { type TotalsCell, sumBy, TotalsRow } from 'components/TableTotals'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { getGridConfig, saveGridConfig } from 'services'
import { Pagination } from 'stories/components'

export interface GridColumn<T = any> {
  key: string
  label: string
  sortable?: boolean
  hideable?: boolean // default true
  reorderable?: boolean // default true
  minWidth?: number // default 60
  defaultWidth?: number // default 150
  align?: 'left' | 'right' | 'center'
  // Total this column in a footer row. `true` sums row[key]; pass a function to sum something
  // the column derives. Ignored for a server-paged grid unless the parent also passes `totals`.
  total?: boolean | ((row: T) => number)
  render?: (row: T) => React.ReactNode // default: String(row[key] ?? '')
}

export interface GridSort {
  key: string
  dir: 'asc' | 'desc'
}

interface DataGridProps<T> {
  // Unique per grid — the per-user column settings (order/widths/hidden/sort) are saved under this key.
  gridKey: string
  columns: GridColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  // Server-side sort: controlled by the parent, which refetches on change. Leave both out and the
  // grid sorts the rows it was given itself, so `sortable` columns work without a server round trip.
  sort?: GridSort | null
  onSortChange?: (sort: GridSort | null) => void
  // Page the rows. Client-side by default: the parent holds every row and only what's rendered is
  // paged. Pass `totalCount` as well and the grid switches to server paging — `rows` is then just
  // the current page, and the parent refetches when onPageChange / onPageSizeChange fire.
  paginate?: boolean
  defaultPageSize?: number
  totalCount?: number
  page?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  onRowClick?: (row: T) => void
  loading?: boolean
  emptyText?: string
  // Optional row selection (adds a locked checkbox column at the front).
  selectable?: boolean
  isRowSelected?: (row: T) => boolean
  onToggleRow?: (row: T) => void
  allSelected?: boolean
  onToggleAll?: () => void
  // Rows the selection must pass over — their checkbox renders disabled. Returning false here is
  // the only thing that stops a row being picked; the header checkbox and any "select all" the
  // parent runs are its own business.
  isRowSelectable?: (row: T) => boolean
  // Extra classes per row, for marking a row out without a column having to do it (a held payout,
  // a row that is only there for context).
  rowClassName?: (row: T) => string
  // Optional expandable rows (adds a locked caret column at the front; renders a
  // full-width detail row beneath each expanded row).
  expandable?: boolean
  isRowExpanded?: (row: T) => boolean
  onToggleExpand?: (row: T) => void
  renderExpanded?: (row: T) => React.ReactNode
  // Column sums by column key, for a SERVER-paged grid: the grid only holds one page, so it
  // cannot work out a total for the whole set and the server has to supply it.
  totals?: Record<string, number>
}

export function DataGrid<T>({
  gridKey,
  columns,
  rows,
  rowKey,
  sort = null,
  onSortChange,
  paginate,
  defaultPageSize = 50,
  totalCount,
  page: pageProp,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  loading,
  emptyText = 'No records.',
  selectable,
  isRowSelected,
  onToggleRow,
  allSelected,
  onToggleAll,
  isRowSelectable,
  rowClassName,
  expandable,
  isRowExpanded,
  onToggleExpand,
  renderExpanded,
  totals,
}: DataGridProps<T>) {
  const colMap = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns])
  const allKeys = useMemo(() => columns.map((c) => c.key), [columns])

  const [order, setOrder] = useState<string[]>(allKeys)
  const [widths, setWidths] = useState<Record<string, number>>({})
  const [hidden, setHidden] = useState<string[]>([])
  const [innerSort, setInnerSort] = useState<GridSort | null>(null)
  const [innerPage, setInnerPage] = useState(0)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [loaded, setLoaded] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const saveTimer = useRef<any>(null)

  // Without an onSortChange the parent isn't refetching, so the grid owns the sort itself.
  const sortsItself = !onSortChange
  const activeSort = sortsItself ? innerSort : sort
  const changeSort = (next: GridSort | null) => (sortsItself ? setInnerSort(next) : onSortChange?.(next))

  // Server paging: `rows` is one page of `totalCount`, and the parent owns which page that is.
  const serverPaged = !!paginate && totalCount !== undefined
  const setPage = (next: number) => (serverPaged ? onPageChange?.(next) : setInnerPage(next))

  // Load the user's saved settings for this grid.
  useEffect(() => {
    let cancelled = false
    getGridConfig(gridKey)
      .then((res) => {
        if (cancelled) return
        const cfg = res?.config || {}
        const savedOrder: string[] = Array.isArray(cfg.order) ? cfg.order : []
        // Keep saved order (existing keys only), then append any new columns.
        const merged = savedOrder.filter((k) => allKeys.includes(k))
        allKeys.forEach((k) => !merged.includes(k) && merged.push(k))
        setOrder(merged)
        setWidths(cfg.widths && typeof cfg.widths === 'object' ? cfg.widths : {})
        setHidden(Array.isArray(cfg.hidden) ? cfg.hidden.filter((k: string) => colMap[k]?.hideable !== false) : [])
        // Restoring a saved page size has to reach the parent too, or a server-paged grid would
        // render 25 rows while still asking the server for 50.
        if (cfg.pageSize > 0 && cfg.pageSize !== pageSize) {
          setPageSize(cfg.pageSize)
          onPageSizeChange?.(cfg.pageSize)
        }
        if (cfg.sort?.key) changeSort(cfg.sort)
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line
  }, [gridKey])

  // Persist settings (debounced) after they change.
  useEffect(() => {
    if (!loaded) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      saveGridConfig(gridKey, { order, widths, hidden, sort: activeSort, pageSize }).catch(() => {})
    }, 500)
    // eslint-disable-next-line
  }, [order, widths, hidden, activeSort, pageSize, loaded])

  // Close the columns menu on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Sort the given rows when nobody upstream is doing it. Numbers compare numerically, everything
  // else as text, and blanks sort last either way so empty cells never lead the grid.
  const sortedRows = useMemo(() => {
    if (!sortsItself || !activeSort) return rows
    const { key, dir } = activeSort
    const factor = dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = (a as any)[key]
      const bv = (b as any)[key]
      const aBlank = av === null || av === undefined || av === ''
      const bBlank = bv === null || bv === undefined || bv === ''
      if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1
      if (typeof av === 'number' && typeof bv === 'number') return factor * (av - bv)
      if (typeof av === 'boolean' && typeof bv === 'boolean') return factor * (Number(av) - Number(bv))
      return factor * String(av).localeCompare(String(bv), undefined, { numeric: true })
    })
  }, [rows, sortsItself, activeSort])

  const pagedCount = serverPaged ? (totalCount as number) : sortedRows.length
  const pageCount = paginate ? Math.max(1, Math.ceil(pagedCount / pageSize)) : 1
  // Rows can disappear underneath us (paid, refreshed) — fall back to the last page that exists
  // rather than rendering an empty one.
  const currentPage = Math.min(serverPaged ? pageProp ?? 0 : innerPage, pageCount - 1)
  useEffect(() => {
    if ((serverPaged ? pageProp ?? 0 : innerPage) > pageCount - 1) setPage(pageCount - 1)
    // eslint-disable-next-line
  }, [pageProp, innerPage, pageCount])

  // In server mode the page has already been sliced by the query that fetched it.
  const pagedRows =
    paginate && !serverPaged ? sortedRows.slice(currentPage * pageSize, currentPage * pageSize + pageSize) : sortedRows

  const visibleColumns = order.map((k) => colMap[k]).filter((c): c is GridColumn<T> => !!c && !hidden.includes(c.key))

  // A totals row appears as soon as any visible column asks for one. Client-paged grids hold every
  // row, so the total covers the whole set; a server-paged grid only has the page in hand and falls
  // back to the `totals` the parent was given by the server.
  const totalsCells = useMemo(() => {
    if (!visibleColumns.some((c) => c.total) || !sortedRows.length) return null
    const leading = (expandable ? 1 : 0) + (selectable ? 1 : 0)
    const cells: TotalsCell[] = Array(leading).fill('')
    visibleColumns.forEach((c) => {
      if (!c.total) {
        cells.push('')
        return
      }
      const fromServer = totals?.[c.key]
      const value =
        fromServer !== undefined
          ? fromServer
          : sumBy(sortedRows, typeof c.total === 'function' ? (c.total as (row: T) => number) : (c.key as keyof T))
      cells.push({ value, className: 'px-3 py-2 text-right tabular-nums' })
    })
    // The label goes in the first column, unless that column is itself a money column - then it
    // takes the leading control cell, and if there is no control cell it is dropped rather than
    // displacing a figure.
    const labelAt = visibleColumns[0]?.total ? (leading ? 0 : -1) : leading
    if (labelAt >= 0) cells[labelAt] = 'Totals'
    return cells
  }, [visibleColumns, sortedRows, totals, expandable, selectable])

  const colWidth = (c: GridColumn<T>) => widths[c.key] ?? c.defaultWidth ?? 150

  const cycleSort = (key: string) => {
    setPage(0)
    if (activeSort?.key !== key) changeSort({ key, dir: 'asc' })
    else if (activeSort.dir === 'asc') changeSort({ key, dir: 'desc' })
    else changeSort(null)
  }

  const onDropColumn = (targetKey: string) => {
    if (!dragKey || dragKey === targetKey) return
    if (colMap[targetKey]?.reorderable === false) return
    setOrder((prev) => {
      const next = prev.filter((k) => k !== dragKey)
      const idx = next.indexOf(targetKey)
      next.splice(idx < 0 ? next.length : idx, 0, dragKey)
      return next
    })
    setDragKey(null)
    setDragOverKey(null)
  }

  const startResize = (key: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = widths[key] ?? colMap[key]?.defaultWidth ?? 150
    const min = colMap[key]?.minWidth ?? 60
    const onMove = (ev: MouseEvent) => {
      setWidths((prev) => ({ ...prev, [key]: Math.max(min, startW + (ev.clientX - startX)) }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
    }
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const resetLayout = () => {
    setOrder(allKeys)
    setWidths({})
    setHidden([])
    changeSort(null)
    setPageSize(defaultPageSize)
    onPageSizeChange?.(defaultPageSize)
    setPage(0)
    setMenuOpen(false)
  }

  const hideableColumns = columns.filter((c) => c.hideable !== false)

  return (
    <div className="relative">
      {/* Toolbar: columns menu */}
      <div className="flex justify-end mb-2">
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="flex items-center gap-1.5 rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-shade-blue hover:text-shade-blue"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" /> Columns
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
              <div className="max-h-72 overflow-auto py-1">
                {hideableColumns.map((c) => (
                  <label
                    key={c.key}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={!hidden.includes(c.key)}
                      onChange={() =>
                        setHidden((prev) => (prev.includes(c.key) ? prev.filter((k) => k !== c.key) : [...prev, c.key]))
                      }
                    />
                    <span className="truncate">{c.label}</span>
                  </label>
                ))}
              </div>
              <div className="border-t border-slate-100 px-3 py-1.5">
                <button type="button" className="text-xs text-shade-blue hover:underline" onClick={resetLayout}>
                  Reset to default
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="text-sm" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
          <colgroup>
            {expandable && <col style={{ width: 36 }} />}
            {selectable && <col style={{ width: 40 }} />}
            {visibleColumns.map((c) => (
              <col key={c.key} style={{ width: colWidth(c) }} />
            ))}
          </colgroup>
          <thead className="bg-slate-50 text-sm font-semibold text-slate-600">
            <tr>
              {expandable && <th className="px-2 py-2" />}
              {selectable && (
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!!allSelected}
                    onChange={onToggleAll}
                    title={serverPaged ? 'Select the rows on this page' : 'Select all'}
                  />
                </th>
              )}
              {visibleColumns.map((c) => {
                const sorted = activeSort?.key === c.key
                return (
                  <th
                    key={c.key}
                    className={`relative select-none px-3 py-2 text-left ${
                      dragOverKey === c.key ? 'bg-sky-100' : ''
                    }`}
                    draggable={c.reorderable !== false}
                    onDragStart={() => c.reorderable !== false && setDragKey(c.key)}
                    onDragOver={(e) => {
                      if (dragKey) {
                        e.preventDefault()
                        setDragOverKey(c.key)
                      }
                    }}
                    onDrop={() => onDropColumn(c.key)}
                    onDragEnd={() => {
                      setDragKey(null)
                      setDragOverKey(null)
                    }}
                  >
                    <button
                      type="button"
                      className={`flex w-full items-center gap-1 ${c.align === 'right' ? 'justify-end' : ''} ${
                        c.sortable ? 'cursor-pointer hover:text-slate-800' : 'cursor-default'
                      }`}
                      onClick={() => c.sortable && cycleSort(c.key)}
                    >
                      <span className="truncate">{c.label}</span>
                      {c.sortable && sorted && (
                        <ArrowUpIcon
                          className={`h-3 w-3 shrink-0 text-shade-blue ${
                            activeSort?.dir === 'desc' ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>
                    {/* resize handle */}
                    <span
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-shade-blue/30"
                      onMouseDown={(e) => startResize(c.key, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="text-[14px] text-gray-900">
            {pagedRows.map((row) => {
              const expanded = expandable && isRowExpanded?.(row)
              return (
                <Fragment key={rowKey(row)}>
                  <tr
                    className={`border-t border-slate-100 hover:bg-sky-50 ${
                      onRowClick ? 'cursor-pointer' : ''
                    } ${rowClassName?.(row) || ''}`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {expandable && (
                      <td className="px-2 py-2 text-slate-400">
                        <button
                          type="button"
                          aria-label={expanded ? 'Collapse row' : 'Expand row'}
                          className="flex items-center justify-center hover:text-shade-blue"
                          onClick={(e) => {
                            e.stopPropagation()
                            onToggleExpand?.(row)
                          }}
                        >
                          <ChevronRightIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                        </button>
                      </td>
                    )}
                    {selectable && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!isRowSelected?.(row)}
                          disabled={isRowSelectable ? !isRowSelectable(row) : false}
                          className={isRowSelectable && !isRowSelectable(row) ? 'cursor-not-allowed' : ''}
                          onChange={(e) => {
                            e.stopPropagation()
                            onToggleRow?.(row)
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    {visibleColumns.map((c) => (
                      <td key={c.key} className="truncate px-3 py-2" style={{ textAlign: c.align }}>
                        {c.render ? c.render(row) : String((row as any)[c.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                  {expanded && renderExpanded && (
                    <tr className="bg-slate-50/60">
                      <td colSpan={visibleColumns.length + (selectable ? 1 : 0) + 1} className="px-6 py-3">
                        {renderExpanded(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
            {!loading && pagedRows.length === 0 && (
              <tr>
                <td
                  colSpan={visibleColumns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0)}
                  className="px-3 py-8 text-center text-gray-400"
                >
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
          {totalsCells && <TotalsRow cells={totalsCells} tdClass="px-3 py-2" />}
        </table>
      </div>

      {paginate && pagedCount > 0 && (
        <Pagination
          totalCount={pagedCount}
          itemCountPerPage={pageSize}
          pageNum={currentPage}
          onNavigate={setPage}
          onCountPerPageUpdate={(count: number) => {
            setPageSize(count)
            onPageSizeChange?.(count)
            setPage(0)
          }}
        />
      )}
    </div>
  )
}
